import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';

const STATUS_COLORS = {
  vacant_clean: '#4caf50',
  vacant_dirty: '#ff9800',
  occupied: '#f44336',
  maintenance: '#ffeb3b',
};

// Sample dynamic data for demonstration
const ROOM_DETAILS = {
  vacant_clean: {
    message: 'Room is clean and ready for new guest.',
  },
  vacant_dirty: {
    lastCheckout: '2025-07-28 11:00 AM',
    previousGuest: 'Alice Johnson',
    cleaningPriority: 'High',
    assignedCleaner: 'John Doe',
  },
  occupied: {
    currentGuest: 'Bob Smith',
    checkInDate: '2025-07-25',
    expectedCheckout: '2025-07-30',
  },
  maintenance: {
    issue: 'Plumbing leak in bathroom',
    reportedOn: '2025-07-27',
    repairStatus: 'Parts ordered, awaiting delivery',
    estimatedFixDate: '2025-08-03',
  },
};

const RoomIcon = ({ room }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const color = STATUS_COLORS[room.current_status] || '#9e9e9e';
  const details = ROOM_DETAILS[room.current_status] || {};

  return (
    <>
      <Tooltip title={`Room ${room.room_number} - ${room.current_status.replace('_', ' ')}`}>
        <Box
          onClick={handleClickOpen}
          sx={{
            width: 70,
            height: 70,
            bgcolor: color,
            borderRadius: 3,
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            color: (theme) => theme.palette.getContrastText(color),
            fontWeight: 'bold',
            fontSize: '1.5rem',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: 6,
            },
          }}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleClickOpen();
          }}
          aria-label={`Details for Room ${room.room_number}`}
        >
          {room.room_number}
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} aria-labelledby="room-dialog-title">
        <DialogTitle id="room-dialog-title">Room {room.room_number} Details</DialogTitle>
        <DialogContent dividers>
          {room.current_status === 'vacant_clean' && (
            <Typography>{details.message}</Typography>
          )}

          {room.current_status === 'vacant_dirty' && (
            <>
              <Typography variant="body1" gutterBottom>
                This room requires cleaning.
              </Typography>
              <Typography><strong>Last Checkout:</strong> {details.lastCheckout}</Typography>
              <Typography><strong>Previous Guest:</strong> {details.previousGuest}</Typography>
              <Typography><strong>Cleaning Priority:</strong> {details.cleaningPriority}</Typography>
              <Typography><strong>Assigned Cleaner:</strong> {details.assignedCleaner}</Typography>
            </>
          )}

          {room.current_status === 'occupied' && (
            <>
              <Typography variant="body1" gutterBottom>
                Currently occupied by guest.
              </Typography>
              <Typography><strong>Guest Name:</strong> {details.currentGuest}</Typography>
              <Typography><strong>Check-In Date:</strong> {details.checkInDate}</Typography>
              <Typography><strong>Expected Checkout:</strong> {details.expectedCheckout}</Typography>
            </>
          )}

          {room.current_status === 'maintenance' && (
            <>
              <Typography variant="body1" gutterBottom>
                Under maintenance.
              </Typography>
              <Typography><strong>Issue:</strong> {details.issue}</Typography>
              <Typography><strong>Reported On:</strong> {details.reportedOn}</Typography>
              <Typography><strong>Status:</strong> {details.repairStatus}</Typography>
              <Typography><strong>Estimated Fix Date:</strong> {details.estimatedFixDate}</Typography>
            </>
          )}

          {!['vacant_clean', 'vacant_dirty', 'occupied', 'maintenance'].includes(room.current_status) && (
            <Typography>No details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoomIcon;
